// @ts-check
/**
 * Storage Manager — the coordinator: one per application, on its main thread.
 *
 * **The developer's brief (2026-09-07, verbatim):** *"every application needs a
 * coordinator, possibly PersistentStorageManager, that is responsible for
 * delegation across the various client storage mediums. Currently, we have a
 * lot of duplication around the same operations, which is only partially
 * addressed with PackFS."* And: *"Persistence is possible, but it is not
 * guaranteed, so we need to make sure our storage management abstraction layer
 * adjusts based on that."*
 *
 * So this is the one place that asks the origin what it is, hands out the
 * folder a purpose gets and the two `Storage` accessors, and tells the other
 * tabs when any of it changed. An application holds one and asks it; it holds
 * no storage decision of its own.
 *
 * | It answers | With |
 * | --- | --- |
 * | what is this origin, now? | `status()` — `persisted()`, `estimate()` and `describeTiers`, read afresh |
 * | may we have persistent storage? | `requestPersistence()` — the single test of `helpers/persistence.js`, from a click |
 * | where does this purpose live? | `folderFor(kind, …)` — the tiers table, then `helpers/storage.js`'s `openStorage` |
 * | where do these settings live? | `settings(namespace)` / `tabState(namespace)` — `SettingsStore` over the two `Storage` tiers |
 * | which medium holds a key somebody else's contract documents? | `keys(tier)` — the literal keys of a tier, format untouched |
 * | who else should know? | `subscribe(listener)` and the topic it publishes on |
 * | how hard should a cleanup prune? | `policyFor(purpose)` — best-effort origins keep less |
 * | how many heavy workers may run, and whose turn is this operation? | `coordination(options)` — `helpers/coordination.js`, one per manager |
 *
 * **Nothing is assumed and nothing is remembered.** `status()` reads the origin
 * on every call — at every start and after every request, which is the
 * developer's ruling D7 — because a persisted origin can become best-effort and
 * an answer kept from five minutes ago is a guess. Concurrent callers share the
 * ONE reading in flight, which is the same call rather than a memory of it;
 * nothing survives it. The only thing held between readings is the previous one,
 * used for nothing but saying WHICH of usage, persistence or the tiers changed.
 *
 * **A scope, not four provisions.** The context is passed in whole — a page
 * passes `globalThis`, a test passes a stand-in — and every provision is read
 * off it here, guarded: a profile with site data blocked throws on the property
 * access itself, not on `getItem`. This is also what keeps an application free
 * of the medium's own names: it hands over its scope rather than reaching for
 * `localStorage`, `sessionStorage`, `indexedDB` or `getDirectory` to pass them
 * in, which is exactly what `helpers/tests/boundary.test.js` forbids it to do.
 *
 * **`persist()` is the main thread's.** It is not exposed in workers (measured,
 * P6), so the ask lives here, once, behind a user activation. A worker may hold
 * a coordinator to read the state and open folders; asking would refuse itself
 * by name.
 *
 * ```js
 * const storage = new PersistentStorageManager({ scope: globalThis, providers });
 * storage.useChannel(endpoint);                       // a hub endpoint, when there is one
 * const status = await storage.status();              // read now, never remembered
 * storage.subscribe(({ kind }) => render(kind));      // this tab and every other one
 * const folder = await storage.folderFor('content', { subdirectory: 'library' });
 * const settings = storage.settings('my-tool-state');
 * ```
 *
 * @module classes/storage/persistent-storage-manager
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Opus 5 (code generation)
 */

import { assertTopic } from '../../helpers/channels.js';
import { Coordinator } from '../coordination/coordinator.js';
import { requestPersistence as runPersistenceTest } from '../../helpers/persistence.js';
import { SettingsStore } from '../settings/settings-store.js';
import { describeTiers, homeOf, openStorage } from '../../helpers/storage.js';
import { PersistenceActivationError } from './storage-errors.js';

/** The topic changes are published on when an application names none. */
export const DEFAULT_TOPIC = 'storage';

/** What a change can be about. Anything else on the topic is refused, not delivered. */
export const CHANGE_KINDS = Object.freeze(['persistence', 'usage', 'tier']);

/**
 * @typedef {{
 *   persisted: boolean | null,
 *   quotaBytes: number | null,
 *   usageBytes: number | null,
 *   isSecureContext: boolean,
 *   tiers: import('../../helpers/storage.js').TiersDescription,
 *   readAt: number,
 * }} StorageStatus
 *   `persisted` is `null` when the question could not be asked at all — which
 *   is not the same as `false`, and a caller that treats it as "not persisted"
 *   is making the safe assumption deliberately rather than being told a lie.
 */

/**
 * @typedef {{
 *   kind: 'persistence' | 'usage' | 'tier',
 *   status: StorageStatus | null,
 *   at: number,
 *   origin: string | null,
 * }} StorageChange
 *   `origin` is the endpoint that published it, or `null` for this context's
 *   own. A change from elsewhere carries the status THAT context read; a
 *   listener that must have this one's asks for it — which is also why a hub
 *   that coalesces two changes on this topic within its window into the latest
 *   (`helpers/channels.js`) costs a listener nothing: the kind says something
 *   moved, and the numbers are read rather than carried.
 */

/**
 * @typedef {{
 *   read(key: string): string | null,
 *   write(key: string, value: string): void,
 *   remove(key: string): void,
 * }} KeyAccessor
 *   The three calls over one tier's literal keys — see `keys(tier)`. It is
 *   deliberately not a `SettingsStore`: what is kept under such a key belongs
 *   to a contract this layer does not own, so the format passes through it
 *   untouched.
 */

/**
 * The DIRECTORY a home's path begins with, or `''` where it names no directory
 * of its own.
 *
 * A home's `path` is a convention relative to the caller's folder, and it comes
 * in three shapes. `derived/<content hash>/` is a directory prefix with a
 * placeholder for the part the caller fills in, so the prefix is `derived`.
 * `.storage/cleanup.json` names a FILE inside the folder, not a folder to open,
 * so it roots nothing — `folderFor` answers the caller's own folder and the file
 * is written into it at the path the table states. `''` is the caller's folder
 * itself.
 *
 * @param {import('../../helpers/storage.js').Home} home
 * @returns {string} without leading or trailing separators
 */
function directoryPrefixOf(home) {
    const stated = `${home.path ?? ''}`;
    if (!stated) return '';
    const placeholder = stated.indexOf('<');
    const literal = placeholder < 0 ? stated : stated.slice(0, placeholder);
    // Only a path that reaches a directory boundary is a directory: anything
    // else is a file name, and rooting a folder at half of one would invent a
    // directory the table never stated.
    return literal.endsWith('/') ? literal.replace(/^\/+|\/+$/gu, '') : '';
}

export class PersistentStorageManager {
    /** @type {any} */
    #scope;

    /** @type {string} */
    #topic;

    /** @type {Record<string, import('../../helpers/storage.js').StorageProvider>} */
    #providers;

    /** @type {string} */
    #label;

    /** @type {(message: string, error?: unknown) => void} */
    #warn;

    /** @type {(options?: any) => Promise<any>} */
    #runPersistenceTest;

    /** @type {{ publish?: Function, subscribe?: Function } | null} */
    #channel = null;

    /** @type {(() => void) | null} */
    #leaveChannel = null;

    /** @type {Set<(change: StorageChange) => void>} */
    #listeners = new Set();

    /** @type {Map<string, SettingsStore>} */
    #stores = new Map();

    /**
     * One accessor per tier of literal keys — see `keys(tier)`. Held for the
     * same reason the stores are: two accessors over one tier is the
     * duplication this layer exists to remove.
     *
     * @type {Map<string, KeyAccessor>}
     */
    #keyAccessors = new Map();

    /**
     * The origin's `Coordinator`, once an application has asked for one — see
     * `coordination(options)`. One per manager, for the same reason there is one
     * store per namespace.
     *
     * @type {Coordinator | null}
     */
    #coordination = null;

    /**
     * The reading in flight, so concurrent callers share one. It is cleared the
     * moment it settles: this is one call, not a cache.
     *
     * @type {Promise<StorageStatus> | null}
     */
    #reading = null;

    /**
     * The previous reading — kept for ONE purpose, to say which of usage,
     * persistence and the tiers changed. It is never answered to a caller;
     * `status()` reads the origin.
     *
     * @type {StorageStatus | null}
     */
    #previous = null;

    #closed = false;

    /**
     * @param {{
     *   scope: any,
     *   topic?: string,
     *   channel?: { publish?: Function, subscribe?: Function } | null,
     *   providers?: Record<string, import('../../helpers/storage.js').StorageProvider>,
     *   label?: string,
     *   warn?: (message: string, error?: unknown) => void,
     *   requestPersistence?: (options?: any) => Promise<any>,
     * }} options
     *   `scope` is the context this coordinator works over — `globalThis` in a
     *   page, a stand-in in a test — and is required: a module that reached for
     *   a global would mean a different thing depending on where it was
     *   imported. `channel` is a hub endpoint (`helpers/channels.js`), or
     *   nothing, in which case changes reach this context's own listeners only.
     *   `requestPersistence` is the single test, injectable so that a test can
     *   drive the coordinator without asking a real browser for a permission it
     *   grants on heuristics no test can produce.
     */
    constructor({
        scope,
        topic = DEFAULT_TOPIC,
        channel = null,
        providers = {},
        label = '[Storage]',
        warn = console.warn,
        requestPersistence = runPersistenceTest,
    } = /** @type {any} */ ({})) {
        if (typeof scope !== 'object' || scope === null) {
            throw new TypeError(
                'A storage coordinator is handed the scope it works over — a page passes globalThis, a test '
                + 'passes a stand-in. Nothing here reaches for a global, because a module that did would '
                + 'answer a different question depending on where it was imported.');
        }
        this.#scope = scope;
        this.#topic = assertTopic(topic);
        this.#providers = providers ?? {};
        this.#label = label;
        this.#warn = warn;
        this.#runPersistenceTest = requestPersistence;
        if (channel) this.useChannel(channel);
    }

    /** The topic changes travel on — the application's own name for them. */
    get topic() { return this.#topic; }

    /** Whether this coordinator has been withdrawn. */
    get closed() { return this.#closed; }

    // ========================================
    // The origin's state
    // ========================================

    /**
     * What this origin is, right now: whether it is persisted, what room there
     * is, whether the context is secure, and which tiers it has.
     *
     * Read from the origin on every call. Concurrent callers share the one
     * reading in flight — the same call, not a memory of it — and nothing
     * survives it.
     *
     * @returns {Promise<StorageStatus>}
     */
    async status() {
        if (this.#reading) return this.#reading;
        this.#reading = this.#read();
        try {
            return await this.#reading;
        } finally {
            this.#reading = null;
        }
    }

    /**
     * Reads the origin again and announces what changed since the last reading:
     * `persistence` when the answer to the question changed, `usage` when the
     * numbers moved, `tier` when a medium came or went. The first refresh has
     * nothing to compare against and announces nothing.
     *
     * `force` announces `usage` anyway — what a caller that has just written
     * something asks for, since it knows what the origin cannot tell it.
     *
     * @param {{ force?: boolean }} [options]
     * @returns {Promise<{ status: StorageStatus, published: readonly string[] }>}
     */
    async refresh({ force = false } = {}) {
        const previous = this.#previous;
        const status = await this.status();

        /** @type {('persistence' | 'usage' | 'tier')[]} */
        const published = [];
        if (previous) {
            if (previous.persisted !== status.persisted) published.push('persistence');
            if (previous.usageBytes !== status.usageBytes || previous.quotaBytes !== status.quotaBytes) {
                published.push('usage');
            }
            // The available tiers are listed in the table's own order, so
            // joining them is a comparison and not a sort.
            if (previous.tiers.available.join(',') !== status.tiers.available.join(',')) published.push('tier');
        }
        if (force && !published.includes('usage')) published.push('usage');

        for (const kind of published) this.#announce(kind, status);
        return { status, published: Object.freeze(published) };
    }

    /**
     * The single test of `helpers/persistence.js`, from a user activation.
     *
     * Refused BY NAME when the context says there is no activation: every
     * engine consults it, so asking without one spends the request and teaches
     * nobody anything. Where a browser exposes no `userActivation` at all the
     * ask proceeds — an absent instrument is not a reading.
     *
     * The state is read again AFTER the request (D7) and travels back with the
     * report, so a caller renders what the browser now says rather than what it
     * hoped the request would achieve; the change is announced to the other
     * tabs, which are looking at the same origin.
     *
     * @param {{ askNotifications?: boolean }} [options]
     * @returns {Promise<any>} the persistence report, with `status` beside it
     */
    async requestPersistence(options = {}) {
        const activation = this.#activation();
        if (activation && activation.isActive !== true) throw new PersistenceActivationError();

        const report = await this.#runPersistenceTest(options);
        const status = await this.status();
        this.#announce('persistence', status);
        return { ...report, status };
    }

    /**
     * How hard a caller should work to keep space free, given what the origin
     * is right now. A best-effort origin keeps derived data bounded; a
     * persisted one may keep it generously — the developer's own rule.
     *
     * The state is read for the answer rather than remembered, for the same
     * reason `status()` is.
     *
     * @param {string} purpose the caller's own word for what it is deciding
     * @returns {Promise<{ purpose: string, persisted: boolean, aggressive: boolean }>}
     */
    async policyFor(purpose) {
        const named = `${purpose ?? ''}`;
        if (!named) throw new TypeError('A policy is asked for by purpose — name what is being decided.');
        const status = await this.status();
        return Object.freeze({
            purpose: named,
            persisted: status.persisted === true,
            aggressive: status.persisted !== true,
        });
    }

    // ========================================
    // Where a datum lives
    // ========================================

    /**
     * The home the tiers table states for a kind — so a caller that already has
     * a coordinator need not import the table beside it.
     *
     * @param {string} kind
     * @returns {import('../../helpers/storage.js').Home}
     */
    homeFor(kind) {
        return homeOf(kind);
    }

    /**
     * The folder a purpose gets.
     *
     * The kind is the layer's own vocabulary (`helpers/storage.js`); the
     * subdirectory is the application's, because this layer never names one.
     * The whole option bag reaches the provider, so what a provider needs — a
     * grant, a base URL, a key — is never this layer's business.
     *
     * **The home's own directory comes first, and the caller never spells it.**
     * The tiers table states where a kind lives; a caller that had to write
     * `derived/` itself would be holding that decision, which is exactly what
     * the table exists to take off it. So the path opened is the home's
     * directory with the caller's subdirectory beneath it, and the folder is
     * NAMED for what was opened, so a caller can say where it is:
     *
     * | Asked | Home states | Folder opened |
     * | --- | --- | --- |
     * | `folderFor('content')` | `''` | the caller's own folder — unchanged |
     * | `folderFor('content', { subdirectory: 'library' })` | `''` | `library` |
     * | `folderFor('derived')` | `derived/<content hash>/` | `derived` |
     * | `folderFor('derived', { subdirectory: '9f86d0…' })` | the same | `derived/9f86d0…` |
     * | `folderFor('journal')` | `.storage/cleanup.json` | the caller's own folder |
     *
     * The last row is the one worth reading twice: a home whose path names a
     * FILE names a file **inside** the folder, not a folder to open, so it roots
     * nothing and the file is written at the path the table states. Rooting a
     * folder at half a file name would invent a directory nobody decided.
     *
     * A kind that does not live in a folder is refused by name, saying what to
     * ask instead: putting settings in a folder because a folder was what was
     * asked for is exactly the kind of decision the tiers table exists to stop.
     *
     * @param {string} kind
     * @param {{ subdirectory?: string, prefer?: string } & Record<string, any>} [options]
     * @returns {Promise<import('../../helpers/storage.js').Folder>}
     */
    async folderFor(kind, options = {}) {
        const home = homeOf(kind);
        if (home.tier !== 'opfs') {
            const accessor = home.tier === 'local' ? 'settings(namespace)'
                : home.tier === 'session' ? 'tabState(namespace)'
                    : 'the one store that holds it';
            throw new TypeError(
                `“${kind}” lives on ${home.medium}, not in a folder — ask ${accessor} for it.`);
        }
        const { subdirectory, prefer = 'opfs', ...rest } = options;
        // The home's own directory prefix comes FIRST, so an application never
        // spells one: asking for `derived` is what puts a folder under
        // `derived/`, and where that is stated is the tiers table.
        const at = [directoryPrefixOf(home), subdirectory]
            .map(part => `${part ?? ''}`.replace(/^\/+|\/+$/gu, ''))
            .filter(Boolean)
            .join('/');
        return openStorage({ ...rest, subdirectory: at || undefined, prefer, providers: this.#providers });
    }

    /**
     * The durable settings tier: one `SettingsStore` over the `localStorage`
     * this coordinator was handed.
     *
     * One namespace is one store, whoever asks — two accessors over one tier is
     * the duplication this layer exists to remove.
     *
     * @param {string} namespace the key the application's state is held under
     * @param {{
     *   adopt?: Record<string, string>,
     *   label?: string,
     *   version?: string | number,
     * }} [options] `adopt` names raw keys an earlier revision wrote outside any
     *   store — `{ name: 'legacy-key' }`; each is folded into the state once,
     *   as the value it holds, and removed, so nothing an operator had is lost
     *   on the way in and nothing is left behind afterwards.
     * @returns {SettingsStore}
     */
    settings(namespace, options = {}) {
        return this.#storeFor('local', namespace, options);
    }

    /**
     * The per-tab tier: one `SettingsStore` over the `sessionStorage` this
     * coordinator was handed — what the operator is looking at, which survives
     * a reload and dies with the tab.
     *
     * @param {string} namespace
     * @param {Parameters<PersistentStorageManager['settings']>[1]} [options]
     * @returns {SettingsStore}
     */
    tabState(namespace, options = {}) {
        return this.#storeFor('session', namespace, options);
    }

    /**
     * The literal keys of a tier — for state whose **format and key name belong
     * to another contract**, which this layer must not reshape.
     *
     * `settings()` and `tabState()` are how an application keeps its own state,
     * and they are the accessors to reach for: one JSON object per namespace,
     * versioned, with the migration rules that go with it. This is not a second
     * one. It exists for the case where a key and its format are **documented
     * elsewhere and pasted by hand** — where an operator is told to write a
     * particular value under a particular name, and the layer's job is to say
     * which medium that name lives in and nothing else.
     *
     * The one such case today: a daemon grant. `conres-daemon --grant` prints an
     * instruction naming its key, `daemon/README.md` documents it, and the value
     * is an array — which `SettingsStore` refuses by design, because an array is
     * not a state. Putting it behind `settings()` would silently break a
     * workflow the developer uses. So the coordinator names the medium, the
     * other contract keeps its format, and neither knows about the other.
     *
     * **A caller reaching for this to avoid a version is doing the wrong thing.**
     * If the format is yours, it is `settings()`.
     *
     * The three calls are the shape a consumer of documented keys needs, and
     * they are guarded exactly as the rest of this class is: a profile with site
     * data blocked has no medium, and that is not an error — reads answer
     * `null`, writes are warned about and dropped, and the session keeps working
     * without being remembered.
     *
     * @param {'local' | 'session'} tier
     * @returns {KeyAccessor}
     */
    keys(tier) {
        if (tier !== 'local' && tier !== 'session') {
            throw new TypeError(
                `A tier of literal keys is “local” or “session” — “${tier}” is neither. `
                + 'A folder is folderFor(kind); an application’s own state is settings() or tabState().');
        }
        const held = this.#keyAccessors.get(tier);
        if (held) return held;

        const name = tier === 'session' ? 'sessionStorage' : 'localStorage';
        /** @param {string} key */
        const assertKey = key => {
            if (typeof key !== 'string' || !key.length) {
                throw new TypeError('A literal key is a non-empty string — name what is being kept.');
            }
            return key;
        };

        // **A bad key and a refusing medium are not the same thing**, and the
        // guard below must not turn one into the other. A key that is not a key
        // is the caller's defect and is thrown, before a medium is touched; a
        // medium that refuses is news about the browser and is warned about.
        // Asserting inside the `try` swallowed the first into the second and
        // answered `null` to a caller that had made a mistake — measured by
        // this layer's own kit, which is why the assertion stands outside.
        const accessor = {
            read: key => {
                assertKey(key);
                const storage = /** @type {Storage | null} */ (this.#pick(name));
                try {
                    return storage?.getItem(key) ?? null;
                } catch (error) {
                    this.#warn(`${this.#label} “${key}” could not be read from ${name}:`, error);
                    return null;
                }
            },
            write: (key, value) => {
                assertKey(key);
                const storage = /** @type {Storage | null} */ (this.#pick(name));
                try {
                    storage?.setItem(key, `${value}`);
                } catch (error) {
                    // A full or blocked medium is not a failure of the caller's
                    // operation — it means this will not be remembered.
                    this.#warn(`${this.#label} “${key}” could not be written to ${name}:`, error);
                }
            },
            remove: key => {
                assertKey(key);
                const storage = /** @type {Storage | null} */ (this.#pick(name));
                try {
                    storage?.removeItem(key);
                } catch (error) {
                    this.#warn(`${this.#label} “${key}” could not be removed from ${name}:`, error);
                }
            },
        };
        this.#keyAccessors.set(tier, accessor);
        return accessor;
    }

    // ========================================
    // Across the tabs
    // ========================================

    /**
     * The origin's `Coordinator` (`helpers/coordination.js`): who leads, how
     * many heavy workers may run at once across every tab, and whose turn a
     * storage operation is.
     *
     * **One per manager.** The first call builds it over the scope this manager
     * was handed and the channel it already holds; every later call answers the
     * same one, and its options are ignored — two coordinators over one name
     * would contend for one lock and split one ledger in half, which is the
     * duplication this layer exists to remove.
     *
     * **The name defaults to this manager's topic**, so two applications sharing
     * one origin's storage share one coordination by default — which is what
     * they want, because they share the storage the cap and the queue are about.
     * An application that must coordinate separately passes its own `name`.
     *
     * The state travels on `helpers/coordination.js`'s own topic, not this
     * manager's: a coordination change is not a storage change, and delivering
     * one where the other is expected is what `subscribe()` refuses by name.
     *
     * @param {{
     *   name?: string,
     *   topic?: string,
     *   caps?: Record<string, number>,
     *   defaultCap?: number,
     *   journal?: import('../coordination/coordinator.js').OperationJournal | null,
     *   contextName?: string | null,
     * }} [options]
     * @returns {Coordinator}
     */
    coordination(options = {}) {
        if (this.#coordination) return this.#coordination;
        const { name = this.#topic, ...rest } = options;
        this.#coordination = new Coordinator({
            ...rest,
            name,
            scope: this.#scope,
            channel: this.#channel,
            label: this.#label,
            warn: this.#warn,
        });
        return this.#coordination;
    }

    // ========================================
    // Changes
    // ========================================

    /**
     * Listens for changes — this context's own, and every other context's that
     * publishes on the same topic through the same hub.
     *
     * @param {(change: StorageChange) => void} listener
     * @returns {() => void} the function that stops listening
     */
    subscribe(listener) {
        if (typeof listener !== 'function') throw new TypeError('A subscription needs a listener');
        this.#listeners.add(listener);
        let active = true;
        return () => {
            if (!active) return;
            active = false;
            this.#listeners.delete(listener);
        };
    }

    /**
     * Takes the hub endpoint to publish on and listen to, replacing whatever
     * was there.
     *
     * A page whose shared hub failed to start opens a dedicated one instead and
     * has a NEW endpoint; a coordinator holding the first one would go deaf at
     * exactly the moment the page recovered.
     *
     * @param {{ publish?: Function, subscribe?: Function } | null} channel
     */
    useChannel(channel) {
        try {
            this.#leaveChannel?.();
        } catch (error) {
            this.#warn(`${this.#label} The previous endpoint would not let go:`, error);
        }
        this.#leaveChannel = null;
        this.#channel = channel ?? null;
        // The coordination publishes the leader's state on the hub, so it takes
        // the new endpoint too — a page whose shared hub fell back would
        // otherwise go on publishing into the one that went away.
        this.#coordination?.useChannel(this.#channel);
        if (!channel || typeof channel.subscribe !== 'function') return this;
        try {
            const stop = channel.subscribe(this.#topic, (/** @type {any} */ publication) => this.#adopt(publication));
            this.#leaveChannel = typeof stop === 'function' ? stop : null;
        } catch (error) {
            this.#warn(`${this.#label} Could not listen on “${this.#topic}”:`, error);
        }
        return this;
    }

    /**
     * Withdraws the coordinator: it leaves the channel, forgets its listeners,
     * and publishes nothing further. The stores it handed out go on working —
     * they are the caller's, and they hold no connection.
     */
    close() {
        if (this.#closed) return;
        this.#closed = true;
        try {
            this.#leaveChannel?.();
        } catch (error) {
            this.#warn(`${this.#label} The endpoint would not let go:`, error);
        }
        this.#leaveChannel = null;
        this.#channel = null;
        this.#listeners.clear();
        // The coordination holds Web Locks, which nothing else can release: a
        // manager withdrawn without closing it would leave this context leading
        // an origin it has stopped taking part in.
        try {
            this.#coordination?.close();
        } catch (error) {
            this.#warn(`${this.#label} The coordination would not close:`, error);
        }
        this.#coordination = null;
    }

    // ========================================
    // Reading the scope
    // ========================================

    /**
     * One provision, read off the scope. The read itself is guarded: a profile
     * with site data blocked throws on the PROPERTY, so a caller that pulled
     * these off its own global to pass them in would have to catch what it
     * cannot describe.
     *
     * @param {string} name
     */
    #pick(name) {
        try {
            const value = this.#scope[name];
            return value === undefined ? null : value;
        } catch (error) {
            this.#warn(`${this.#label} ${name} could not be read from this context — treating it as absent:`, error);
            return null;
        }
    }

    /** The context's `navigator.storage`, or `null` — the one thing every reading needs. */
    #storageManager() {
        const navigatorLike = this.#pick('navigator');
        try {
            return /** @type {any} */ (navigatorLike)?.storage ?? null;
        } catch (error) {
            this.#warn(`${this.#label} navigator.storage could not be read from this context:`, error);
            return null;
        }
    }

    /** What the context says about the user's activation, or `null` where it says nothing. */
    #activation() {
        const navigatorLike = this.#pick('navigator');
        try {
            return /** @type {any} */ (navigatorLike)?.userActivation ?? null;
        } catch (error) {
            this.#warn(`${this.#label} navigator.userActivation could not be read from this context:`, error);
            return null;
        }
    }

    /** @returns {Promise<StorageStatus>} */
    async #read() {
        const storage = this.#storageManager();

        /** @type {boolean | null} */
        let persisted = null;
        if (typeof storage?.persisted === 'function') {
            try {
                persisted = await storage.persisted() === true;
            } catch (error) {
                this.#warn(`${this.#label} This origin would not say whether it is persisted:`, error);
                persisted = null;
            }
        }

        /** @type {number | null} */
        let quotaBytes = null;
        /** @type {number | null} */
        let usageBytes = null;
        if (typeof storage?.estimate === 'function') {
            try {
                const estimate = await storage.estimate();
                quotaBytes = estimate?.quota ?? null;
                usageBytes = estimate?.usage ?? null;
            } catch (error) {
                this.#warn(`${this.#label} This origin would not say how much room there is:`, error);
            }
        }

        const status = Object.freeze({
            persisted,
            quotaBytes,
            usageBytes,
            isSecureContext: this.#pick('isSecureContext') === true,
            tiers: describeTiers({
                sessionStorage: this.#pick('sessionStorage'),
                localStorage: this.#pick('localStorage'),
                navigator: this.#pick('navigator'),
                indexedDB: this.#pick('indexedDB'),
            }),
            readAt: Date.now(),
        });
        this.#previous = status;
        return status;
    }

    // ========================================
    // The two Storage tiers
    // ========================================

    /**
     * @param {'local' | 'session'} tier
     * @param {string} namespace
     * @param {{ adopt?: Record<string, string>, label?: string, version?: string | number }} options
     * @returns {SettingsStore}
     */
    #storeFor(tier, namespace, { adopt, label, version } = {}) {
        const named = `${namespace ?? ''}`;
        if (!named) throw new TypeError('A store is asked for by namespace — name what is being kept.');
        const key = `${tier}:${named}`;
        const held = this.#stores.get(key);
        if (held) return held;

        const storage = /** @type {Storage | null} */ (
            this.#pick(tier === 'session' ? 'sessionStorage' : 'localStorage'));
        const store = new SettingsStore(named, {
            storage, label: label ?? this.#label, warn: this.#warn, version,
        });
        if (adopt) this.#adoptLegacyKeys(store, storage, adopt);
        this.#stores.set(key, store);
        return store;
    }

    /**
     * Folds keys an earlier revision wrote OUTSIDE any store into this one, and
     * takes them away.
     *
     * A value written as a bare string under a key of its own cannot be read by
     * a store that holds one JSON object, so moving a datum onto this layer
     * without this would quietly discard whatever the operator had — a
     * remembered address, a sign-in that has not been completed yet. It runs
     * once and is safe to ask for on every load: a name the state already
     * carries is left alone.
     *
     * @param {SettingsStore} store
     * @param {Storage | null} storage
     * @param {Record<string, string>} adopt
     */
    #adoptLegacyKeys(store, storage, adopt) {
        const state = store.read() ?? {};
        /** @type {string[]} */
        const taken = [];
        let adopted = 0;

        for (const [name, legacyKey] of Object.entries(adopt)) {
            if (state[name] !== undefined) { taken.push(legacyKey); continue; }
            let value = null;
            try {
                value = storage?.getItem(legacyKey) ?? null;
            } catch (error) {
                this.#warn(`${this.#label} Could not read the earlier “${legacyKey}”:`, error);
                continue;
            }
            if (value === null) continue;
            state[name] = value;
            taken.push(legacyKey);
            adopted++;
        }

        if (adopted) store.write(state);
        if (taken.length) store.removeLegacyKeys(taken);
    }

    // ========================================
    // Announcing
    // ========================================

    /**
     * @param {'persistence' | 'usage' | 'tier'} kind
     * @param {StorageStatus} status
     */
    #announce(kind, status) {
        if (this.#closed) return;
        const at = Date.now();
        this.#deliver({ kind, status, at, origin: null });
        if (typeof this.#channel?.publish !== 'function') return;
        try {
            this.#channel.publish(this.#topic, { kind, status, at });
        } catch (error) {
            this.#warn(`${this.#label} Could not publish a ${kind} change on “${this.#topic}”:`, error);
        }
    }

    /**
     * A publication from another context on this coordinator's topic.
     *
     * @param {any} publication
     */
    #adopt(publication) {
        const data = publication?.data ?? {};
        if (!CHANGE_KINDS.includes(data.kind)) {
            this.#warn(
                `${this.#label} A publication on “${this.#topic}” was not a change of this layer's — `
                + `“${data.kind}” is not one of ${CHANGE_KINDS.join(', ')}; it was not delivered.`);
            return;
        }
        this.#deliver({
            kind: data.kind,
            status: data.status ?? null,
            at: Number(data.at ?? Date.now()),
            origin: publication?.origin ?? null,
        });
    }

    /** @param {StorageChange} change */
    #deliver(change) {
        if (this.#closed) return;
        const frozen = Object.freeze(change);
        for (const listener of [...this.#listeners]) {
            try {
                listener(frozen);
            } catch (error) {
                this.#warn(`${this.#label} A listener threw for a ${change.kind} change:`, error);
            }
        }
    }
}
